import { CountDay, VolumeDay } from "./types";

/**
 * Day → week → month rollups for the message-volume charts, and — since Sep
 * 2026 — for the plain count series on the Instagram and Events screens.
 *
 * The bucketing rules live here once rather than being re-derived per screen,
 * so "week" means the same Monday-start UTC week on every chart in this tool.
 * That mattered enough for two charts; it matters more for six.
 *
 * **Derived on read, not precomputed.** The backend stores one entry per day
 * and nothing else. Grouping ~1,710 of them into weeks or months is a single
 * pass over a small array — microseconds — so precomputing and caching the two
 * coarser series would buy no measurable speed while adding two more things
 * that can silently disagree with the day series they came from. The day rows
 * stay the single source, exactly as `adminPlatformStats` rows are for revenue.
 *
 * Shared by the Home tab's platform chart and the customer detail page's
 * per-customer chart so a week means the same thing on both.
 */

export type Granularity = "day" | "week" | "month";

export const GRANULARITIES: { key: Granularity; label: string }[] = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];

/**
 * How much history each granularity shows by default.
 *
 * The toggle picks the WINDOW as well as the bucket, because the two are the
 * same decision in practice: 1,710 daily bars is an unreadable smear, and six
 * months of monthly bars is six bars. Days answer "what happened this week",
 * months answer "are we growing" — so each granularity opens on the window that
 * question needs. `null` means the whole stored history.
 */
export const DEFAULT_WINDOW_DAYS: Record<Granularity, number | null> = {
  day: 90,
  week: 364,
  month: null,
};

/**
 * The UTC Monday on or before this day.
 *
 * Weeks are Monday-start and labelled by that Monday. UTC throughout, matching
 * every other day boundary in this tool — `dailyTwilioLogs`, the Stripe day
 * bounds and the `adminPlatformStats` document IDs all key off UTC, and a
 * locally-bucketed week here would put a day in a different bucket than the
 * row it came from.
 */
export const startOfUtcWeek = (date: string): string => {
  const parsed = new Date(`${date}T00:00:00.000Z`);
  if (isNaN(parsed.getTime())) return date;
  // getUTCDay(): 0 = Sunday. Shift so Monday is 0.
  const offset = (parsed.getUTCDay() + 6) % 7;
  parsed.setUTCDate(parsed.getUTCDate() - offset);
  return parsed.toISOString().slice(0, 10);
};

/** 'YYYY-MM-01' — a month bucket, keyed so it sorts and parses like a day. */
export const startOfUtcMonth = (date: string): string => `${date.slice(0, 7)}-01`;

export interface VolumeBucket {
  /** The bucket's first UTC day — a real date, so the axis can format it. */
  date: string;
  messagesSent: number;
  spendCents: number;
  /** How many day rows fell in this bucket. Powers the partial-bucket note. */
  dayCount: number;
}

/**
 * Groups day rows into day, week or month buckets, oldest first.
 *
 * Sums rather than averages: "messages sent this month" is a total, and an
 * average would answer a question nobody asked of a volume chart.
 */
export const bucketVolume = (
  days: VolumeDay[],
  granularity: Granularity
): VolumeBucket[] => {
  const keyFor =
    granularity === "day"
      ? (date: string) => date
      : granularity === "week"
      ? startOfUtcWeek
      : startOfUtcMonth;

  const buckets = new Map<string, VolumeBucket>();
  days.forEach(day => {
    const key = keyFor(day.date);
    const existing = buckets.get(key);
    if (existing) {
      existing.messagesSent += day.messagesSent;
      existing.spendCents += day.spendCents;
      existing.dayCount += 1;
      return;
    }
    buckets.set(key, {
      date: key,
      messagesSent: day.messagesSent,
      spendCents: day.spendCents,
      dayCount: 1,
    });
  });

  // `Array.from` rather than spreading the map iterator: this repo's tsconfig
  // targets below es2015 without `downlevelIteration`, so a spread of an
  // iterator does not compile here.
  return Array.from(buckets.values()).sort((left, right) =>
    left.date.localeCompare(right.date)
  );
};

export interface CountBucket {
  /** The bucket's first UTC day — a real date, so the axis can format it. */
  date: string;
  value: number;
  /** How many day rows fell in this bucket. Powers the partial-bucket note. */
  dayCount: number;
}

/**
 * Groups a plain `{date, value}` series into day, week or month buckets.
 *
 * The count counterpart to `bucketVolume`, sharing its key functions so the two
 * cannot drift into different ideas of what a week is. Sums rather than
 * averages, for the same reason: "accounts connected this month" is a total.
 */
export const bucketCounts = (
  days: CountDay[],
  granularity: Granularity
): CountBucket[] => {
  const keyFor =
    granularity === "day"
      ? (date: string) => date
      : granularity === "week"
      ? startOfUtcWeek
      : startOfUtcMonth;

  const buckets = new Map<string, CountBucket>();
  days.forEach(day => {
    const key = keyFor(day.date);
    const existing = buckets.get(key);
    if (existing) {
      existing.value += day.value;
      existing.dayCount += 1;
      return;
    }
    buckets.set(key, { date: key, value: day.value, dayCount: 1 });
  });

  // `Array.from` rather than spreading the map iterator — same tsconfig
  // constraint `bucketVolume` documents.
  return Array.from(buckets.values()).sort((left, right) =>
    left.date.localeCompare(right.date)
  );
};

export interface RevenueBucket {
  /** The bucket's first UTC day — a real date, so the axis can format it. */
  date: string;
  subscriptionCents: number;
  overflowCents: number;
  /** The sum of the two — drawn as its own line, not stacked. */
  totalCents: number;
  /** How many day rows fell in this bucket. Powers the partial-bucket note. */
  dayCount: number;
  /**
   * Stripe's cut on each line, summed over the days in this bucket that
   * actually carry one.
   *
   * Meaningless on their own unless `feeComplete` is true — see below.
   */
  subscriptionFeeCents: number;
  overflowFeeCents: number;
  /** How many of `dayCount` days carry a recorded fee. */
  daysWithFee: number;
  /**
   * True only when EVERY day in the bucket carries a fee.
   *
   * **A partially-measured bucket must not be drawn net, and this flag is why
   * the chart can tell.** A month where three days of thirty have a fee
   * recorded would net out ~10% of the fee it really cost and draw a net line
   * sitting almost on top of gross — a plausible-looking figure that is wrong
   * in the reassuring direction. Those buckets are drawn as a gap instead,
   * exactly as a day with revenue but no deliverability is dropped from the
   * delivery chart rather than counted as four zeros.
   */
  feeComplete: boolean;
}

/**
 * Groups Markit's realized recurring revenue into day, week or month buckets.
 *
 * The third bucketer here, sharing the same key functions as the two above so a
 * "week" cannot mean one thing on the revenue chart and another on the volume
 * chart beside it. Sums rather than averages, same as both: "recurring revenue
 * this month" is a total.
 *
 * `totalCents` is derived here rather than stored, so the three lines are
 * arithmetically guaranteed to agree — a total that came down the wire
 * separately could disagree with the two components a reader can see.
 */
export const bucketRevenue = (
  days: {
    date: string;
    subscriptionCents: number;
    overflowCents: number;
    subscriptionFeeCents?: number | null;
    overflowFeeCents?: number | null;
  }[],
  granularity: Granularity
): RevenueBucket[] => {
  const keyFor =
    granularity === "day"
      ? (date: string) => date
      : granularity === "week"
      ? startOfUtcWeek
      : startOfUtcMonth;

  const buckets = new Map<string, RevenueBucket>();
  days.forEach(day => {
    const key = keyFor(day.date);
    // Null means "Stripe's cut was never recorded for this day", which is not
    // a zero fee. Counted rather than coerced, so `feeComplete` can tell a
    // fully-measured bucket from a partly-measured one.
    const hasFee =
      day.subscriptionFeeCents !== null &&
      day.subscriptionFeeCents !== undefined &&
      day.overflowFeeCents !== null &&
      day.overflowFeeCents !== undefined;
    const subscriptionFee = hasFee ? day.subscriptionFeeCents! : 0;
    const overflowFee = hasFee ? day.overflowFeeCents! : 0;

    const existing = buckets.get(key);
    if (existing) {
      existing.subscriptionCents += day.subscriptionCents;
      existing.overflowCents += day.overflowCents;
      existing.totalCents += day.subscriptionCents + day.overflowCents;
      existing.dayCount += 1;
      existing.subscriptionFeeCents += subscriptionFee;
      existing.overflowFeeCents += overflowFee;
      if (hasFee) existing.daysWithFee += 1;
      existing.feeComplete = existing.daysWithFee === existing.dayCount;
      return;
    }
    buckets.set(key, {
      date: key,
      subscriptionCents: day.subscriptionCents,
      overflowCents: day.overflowCents,
      totalCents: day.subscriptionCents + day.overflowCents,
      dayCount: 1,
      subscriptionFeeCents: subscriptionFee,
      overflowFeeCents: overflowFee,
      daysWithFee: hasFee ? 1 : 0,
      feeComplete: hasFee,
    });
  });

  // `Array.from`, not spread — same tsconfig constraint `bucketVolume` documents.
  return Array.from(buckets.values()).sort((left, right) =>
    left.date.localeCompare(right.date)
  );
};

/**
 * Trims a day series to the granularity's default window.
 *
 * Applied to the DAY rows before bucketing rather than to the buckets after,
 * so a window boundary can't leave a half-filled first bucket masquerading as
 * a real drop.
 *
 * Generic over anything day-keyed since Sep 2026, so the count series get the
 * same treatment without a second copy of the snapping logic. Callers with a
 * genuinely short history — the Instagram charts, whose oldest data is ~3
 * months old because the feature is that new — skip it entirely and chart
 * everything they have; windowing a series shorter than the window is a no-op
 * that only risks trimming the oldest bucket.
 */
export const windowDays = <T extends { date: string }>(
  days: T[],
  granularity: Granularity
): T[] => {
  const span = DEFAULT_WINDOW_DAYS[granularity];
  if (span === null || days.length === 0) return days;

  const newest = days[days.length - 1].date;
  const cutoff = new Date(`${newest}T00:00:00.000Z`);
  cutoff.setUTCDate(cutoff.getUTCDate() - (span - 1));
  const cutoffKey = cutoff.toISOString().slice(0, 10);

  // For week and month the window is snapped OUT to the enclosing bucket, so
  // the oldest bar is a whole week/month rather than a stub that reads as a
  // sudden collapse in volume.
  const snapped =
    granularity === "week"
      ? startOfUtcWeek(cutoffKey)
      : granularity === "month"
      ? startOfUtcMonth(cutoffKey)
      : cutoffKey;

  return days.filter(day => day.date >= snapped);
};

/**
 * The newest bucket is usually incomplete — the rollup runs nightly and stops
 * at yesterday, so the current week or month is a partial total that will keep
 * climbing. Charting it beside finished buckets makes every series look like it
 * just fell off a cliff, so the UI labels it rather than hiding it.
 */
export const isPartialBucket = (
  bucket: { date: string; dayCount: number } | undefined,
  granularity: Granularity
): boolean => {
  if (!bucket || granularity === "day") return false;
  if (granularity === "week") return bucket.dayCount < 7;
  const [year, month] = bucket.date.split("-").map(Number);
  // Day 0 of the next month is the last day of this one.
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return bucket.dayCount < daysInMonth;
};

/** "Sep 2026" / "Week of Sep 1" / "Sep 6" — the tick a bucket wears. */
export const formatBucketTick = (date: string, granularity: Granularity): string => {
  const parsed = new Date(`${date}T00:00:00.000Z`);
  if (isNaN(parsed.getTime())) return date;
  if (granularity === "month") {
    return parsed.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  }
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

/** The longer form a tooltip uses, where "Week of" has room to be said. */
export const formatBucketLabel = (date: string, granularity: Granularity): string => {
  const base = formatBucketTick(date, granularity);
  return granularity === "week" ? `Week of ${base}` : base;
};
